/**
 * DemoBanner
 *
 * Global banner shown at the top of financial pages when the tenant is
 * running against seeded demo data. Warns Gary (and everyone else on
 * the account) that numbers on screen are not real, and provides a
 * one-click "Remove Demo Data" action that flips the app back to the
 * empty-state ConnectCta.
 *
 * Palette: Amber only. No red/green.
 */
import { Info, Loader2, X } from "lucide-react";
import { useState } from "react";
import { useDemoMode } from "../../hooks/useDemoMode";

export function DemoBanner({ onCleared, testId = "demo-mode-banner" }) {
  const { status, busy, clear } = useDemoMode();
  const [confirming, setConfirming] = useState(false);

  if (!status.active || status.quickbooks_live) return null;

  const handleClear = async () => {
    const res = await clear();
    if (res.ok) {
      if (onCleared) onCleared();
      else window.location.reload();
    }
  };

  return (
    <div
      data-testid={testId}
      className="w-full rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 mb-4"
      style={{
        background: "linear-gradient(90deg, #FFF7DB 0%, #FEF3E6 100%)",
        border: "1px solid #D4A017",
      }}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "#FFFFFF", border: "1px solid #D4A017" }}
        >
          <Info size={16} style={{ color: "#8A6D0B" }} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold" style={{ color: "#8A6D0B" }}>
            Demo mode active - numbers on this page are illustrative only
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: "#6B7491" }}>
            Connect QuickBooks to replace demo data with your live financials.
          </p>
        </div>
      </div>

      {!confirming ? (
        <button
          type="button"
          data-testid={`${testId}-remove-btn`}
          onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors flex-shrink-0"
          style={{ background: "#FFFFFF", color: "#8A6D0B", border: "1px solid #D4A017" }}
        >
          <X size={12} />
          Remove Demo Data
        </button>
      ) : (
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            data-testid={`${testId}-remove-cancel-btn`}
            onClick={() => setConfirming(false)}
            className="text-xs px-2 py-1.5 font-semibold"
            style={{ color: "#6B7491" }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid={`${testId}-remove-confirm-btn`}
            onClick={handleClear}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-60"
            style={{ background: "#8A6D0B", color: "#FFFFFF" }}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
            {busy ? "Clearing..." : "Yes, remove demo data"}
          </button>
        </div>
      )}
    </div>
  );
}

export default DemoBanner;
